import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { useAppData, type ObjectType } from "store/appData";
import t from "./translations";

type Props = {
  label: string;
  value: number | null;
  onChange: (typeId: number | null) => void;
  excludeTypeId?: number | null;
};

function optionDetail(option: ObjectType): string {
  const parts = [t.IdLabel(option.id)];
  if (option.skyqraft_only) parts.push(t.InternalLabel);
  if (option.is_sensitive) parts.push(t.SensitiveLabel);
  return parts.join(" · ");
}

export default function ObjectTypePicker({
  label,
  value,
  onChange,
  excludeTypeId,
}: Props) {
  const { objectTypes } = useAppData();

  const options = useMemo(
    () =>
      Object.values(objectTypes)
        .filter((type) => type.id !== excludeTypeId)
        .sort((a, b) => a.en_name.localeCompare(b.en_name)),
    [objectTypes, excludeTypeId],
  );

  const selected = value !== null ? (objectTypes[value] ?? null) : null;

  return (
    <Autocomplete<ObjectType>
      options={options}
      value={selected}
      onChange={(_event, next) => onChange(next?.id ?? null)}
      getOptionLabel={(option) => option.en_name}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      sx={{ width: 320, flexShrink: 0 }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
            <Typography variant="body2">{option.en_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {optionDetail(option)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={selected ? optionDetail(selected) : " "}
        />
      )}
    />
  );
}
